import { MessageCircle, Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { MagneticButton } from "../components/MagneticButton";
import { Reveal } from "../components/Reveal";
import { WA_LINKS, CONTACT } from "../lib/utils";

const CONTACT_OPTIONS = [
{ icon: MessageCircle, label: "WhatsApp Us", desc: "Fastest response. Our team typically replies within 2 hours.", action: "Chat on WhatsApp- 9958150131", href: WA_LINKS.general, accent: "emerald", external: true },
{ icon: Phone, label: "Call Us", desc: "Speak directly with a property expert. Available Mon–Sat, 9am–7pm IST.", action: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, accent: "navy", external: false },
{ icon: Mail, label: "Email Us", desc: "For detailed inquiries, documents, or non-urgent matters.", action: CONTACT.email, href: `mailto:${CONTACT.email}`, accent: "gold", external: false }];


const WHATSAPP_PROMPTS = [
{ label: "Free Property Health Check", desc: "Get a thorough assessment of your property — at no cost.", href: WA_LINKS.healthCheck },
{ label: "Book a 15-min Expert Call", desc: "Speak to a property expert about your specific situation.", href: WA_LINKS.call },
{ label: "General Enquiry", desc: "Questions about our services, pricing, or coverage.", href: WA_LINKS.general }];


export default function ContactPage() {
  return (
    <>
      <section className="relative bg-stone-50 pt-40 pb-24 overflow-hidden" data-testid="contact-hero">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-30" style={{ backgroundImage: `radial-gradient(circle, #0A254018 1px, transparent 1px)`, backgroundSize: "28px 28px" }} />
        <div className="container-luxury text-center">
          <Reveal><p className="eyebrow mb-5">Get In Touch</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-sans text-display-lg font-semibold text-navy-800 tracking-tight text-balance leading-tight mx-auto max-w-2xl">
              Let's Talk About <span className="font-display italic font-normal text-emerald-500">Your Property</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg text-navy-600/70 max-w-xl mx-auto text-balance leading-relaxed">Whether you're ready to get started or just exploring your options, we're here and we respond fast.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="contact-channels">
        <div className="container-luxury">
          <div className="grid gap-6 md:grid-cols-3">
            {CONTACT_OPTIONS.map((opt, i) => {
              const Icon = opt.icon;
              return (
                <Reveal key={opt.label} delay={i * 0.1}>
                  <a
                    href={opt.href}
                    target={opt.external ? "_blank" : undefined}
                    rel={opt.external ? "noopener noreferrer" : undefined}
                    className={`group flex flex-col gap-4 rounded-2xl p-8 border transition-all duration-300 hover:shadow-luxury ${opt.accent === "emerald" ? "border-emerald-200 bg-emerald-50 hover:bg-emerald-400 hover:border-emerald-400" : opt.accent === "gold" ? "border-gold-200 bg-gold-50 hover:bg-gold-400 hover:border-gold-400" : "border-navy-200 bg-navy-50 hover:bg-navy-800 hover:border-navy-800"}`}
                    data-testid={`contact-${opt.label.toLowerCase().replace(/\s/g, "-")}`}>

                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center transition-colors duration-300 ${opt.accent === "emerald" ? "bg-emerald-100 text-emerald-600 group-hover:bg-white/20 group-hover:text-white" : opt.accent === "gold" ? "bg-gold-100 text-gold-600 group-hover:bg-white/20 group-hover:text-white" : "bg-navy-100 text-navy-700 group-hover:bg-white/20 group-hover:text-white"}`}>
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-semibold tracking-tight transition-colors duration-300 text-navy-800 group-hover:text-white">{opt.label}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed transition-colors duration-300 text-navy-600/70 group-hover:text-white/80">{opt.desc}</p>
                    </div>
                    <span className="mt-auto text-sm font-medium transition-colors duration-300 text-navy-700 group-hover:text-white">{opt.action} →</span>
                  </a>
                </Reveal>);

            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-navy-800 noise-overlay" data-testid="whatsapp-section">
        <div className="container-luxury">
          <SectionHeading eyebrow="Quick Start" title="What brings you here" titleAccent="today?" subtitle="Tap the option that matches your need and we'll pick up the conversation on WhatsApp immediately." dark />
          <div className="mt-16 grid gap-5 md:grid-cols-3">
            {WHATSAPP_PROMPTS.map((p, i) =>
            <Reveal key={p.label} delay={i * 0.1}>
                <a href={p.href} target="_blank" rel="noopener noreferrer" className="group flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-7 hover:bg-emerald-400 hover:border-emerald-300 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <MessageCircle size={20} className="text-emerald-400 group-hover:text-white transition-colors" />
                    <ArrowRight size={16} className="text-white/30 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white tracking-tight">{p.label}</h3>
                    <p className="mt-1.5 text-sm text-white/60 group-hover:text-white/90 transition-colors leading-relaxed">{p.desc}</p>
                  </div>
                </a>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-luxury">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <Reveal>
              <div className="rounded-2xl border border-navy-100 bg-white p-8 h-full">
                <div className="h-11 w-11 rounded-xl bg-navy-50 flex items-center justify-center text-navy-700 mb-5"><MapPin size={20} strokeWidth={1.5} /></div>
                <h3 className="font-semibold text-navy-800 tracking-tight">Headquarters</h3>
                <p className="mt-3 text-sm text-navy-600/70 leading-relaxed">New Delhi, India<br />Operations across 7 major cities</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-navy-100 bg-white p-8 h-full">
                <div className="h-11 w-11 rounded-xl bg-navy-50 flex items-center justify-center text-navy-700 mb-5"><Clock size={20} strokeWidth={1.5} /></div>
                <h3 className="font-semibold text-navy-800 tracking-tight">Office Hours</h3>
                <div className="mt-3 text-sm text-navy-600/70 leading-relaxed space-y-1">
                  <p>Mon – Fri: 9:00 am – 7:00 pm IST</p>
                  <p>Saturday: 10:00 am – 5:00 pm IST</p>
                  <p className="text-emerald-600 font-medium">WhatsApp: Always On</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="rounded-2xl bg-navy-800 p-8 h-full noise-overlay">
                <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center text-emerald-400 mb-5"><MessageCircle size={20} strokeWidth={1.5} /></div>
                <h3 className="font-semibold text-white tracking-tight">Our Response Promise</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">WhatsApp messages answered within 2 hours during business hours. Email queries within 24 hours. Emergencies escalated immediately to our on-ground team.</p>
                <div className="mt-6">
                  <MagneticButton href={WA_LINKS.general} external size="md" variant="ghost" className="border-white/20 text-white hover:border-white/50">Message Us Now</MagneticButton>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>);

}